import Highcharts from 'highcharts';
import { Component, effect, inject, input } from '@angular/core';
import { TourMatMenu } from 'ngx-ui-tour-md-menu';
import { DataSeries } from 'src/app/models/classes/data-series.class';
import { KeyValuePair } from 'src/app/models/classes/key-value-pair.class';
import { CurrencyFormatPipe } from 'src/app/pipes/currency-format.pipe';
import { GROSVENOR_BLUE, WHITE } from '@gcm/gcm-ui-angular';
import { HighchartsService } from 'src/app/services/highcharts-service.service';
import { BaseWidgetComponent } from 'src/app/shared/base-widget/base-widget.component';
import { GcmChartComponent } from 'src/app/shared/gcm-chart/gcm-chart.component';
import { InfoIconComponent } from 'src/app/shared/info-icon/info-icon.component';

@Component({
    selector: 'app-largest-position-widget',
    imports: [BaseWidgetComponent, InfoIconComponent, GcmChartComponent, TourMatMenu],
    templateUrl: './largest-position-widget.component.html'
})
export class LargestPositionWidgetComponent {
  private highchartsService = inject(HighchartsService);
  private currencyFormat = inject(CurrencyFormatPipe);

  readonly widgets = input<Record<string, KeyValuePair<string | number, number>[]>>();
  readonly currency = input<string>();
  largestPositionsByRemainingValue!: DataSeries;
  largestPositionsByRemainingValueSum = -1;
  largestPositionsByRemainingValueChartOptions = {};
  chartStyle = { width: '99%', display: 'flex' };

  private readonly rebuildChart = effect(() => {
    const widgets = this.widgets();
    if (widgets) {
      this.largestPositionsByRemainingValue = this.highchartsService.buildDataSeries(widgets['largestRemainingValue']?.slice(0, 5), 'Remaining Market Value');
      if (widgets['largestRemainingValue'] && widgets['largestRemainingValue'].length > 0) {
        this.largestPositionsByRemainingValueSum = widgets['largestRemainingValue'].map(a => a.value).reduce((total, val) => total + val, 0);
        this.largestPositionsByRemainingValueChartOptions = this.buildLargestPositionsChartOptions(this.largestPositionsByRemainingValueSum);
      }
    }
  });

  buildLargestPositionsChartOptions(sum: number) {
    const currencyFormat = this.currencyFormat;
    const currency = this.currency();
    if (sum <= 0) {
      this.largestPositionsByRemainingValue.error = this.highchartsService.noResultsText;
      return {};
    }
    return {
      chart: {
        height: 365,
        type: 'funnel',
        marginBottom: 115,
        marginRight: 0
      },
      plotOptions: {
        series: {
          center: ['45%', '50%'],
          neckWidth: '7%',
          neckHeight: '0%',
          width: '35%',
          states: {
            inactive: {
              opacity: 1
            }
          },
          dataLabels: {
            formatter: function (this: Highcharts.Point) {
              const valToShow = ((Math.abs(this.y as number) / Number(sum)) * 100).toFixed(1) + '%';
              return valToShow;
            }
          }
        }
      },
      legend: {
        enabled: true,
        symbolRadius: 0,
        alignColumns: false,
        layout: 'vertical',
        align: 'right',
        itemStyle: {
          fontWeight: '400',
          cursor: 'auto'
        }
      },
      tooltip: {
        formatter: function (this: Highcharts.Point) {
          const valToShow = currencyFormat.transform(this.y, currency);
          const data = `${this.series.name} <br/>` + valToShow + ` <br/><hr class="mt-2 mb-2 mr-0 ml-0" style="background-color: ${WHITE}">`
          const footer = `<div>${this.key}</div>`
          return data + footer
        }
      },
      series: [{
        showInLegend: true,
        allowPointSelect: false,
        colors: [0.2, 0.4, 0.6, 0.8, 1].map(opacity => Highcharts.color(GROSVENOR_BLUE).setOpacity(opacity).get('rgba') as string)
      }]
    }
  }
}
